import { create } from 'zustand';
import { Node, Edge, Connection, addEdge, applyNodeChanges, applyEdgeChanges, NodeChange, EdgeChange } from 'reactflow';
import { propertySchemaRegistry } from '@shared/utils/property-schema-registry';
import { applyDefaultValues } from '@shared/utils/property-validator';
import { validateEdges } from '../utils/edge-validation';
import { inferSchemaFromDefinition } from '../lib/schema-inference';
import { Workflow } from '@/shared/types';

interface ExecutionProgress {
  isExecuting: boolean;
  progress: number;
  completedNodes: number;
  totalNodes: number;
  startTime?: Date;
}

// Workflow metadata needed for context-aware features (e.g., KB collection filtering)
interface WorkflowMetadata {
  id: string;
  name: string;
  workflowLocationType?: number; // 0 = Personal, 1 = Team
  teamFolderId?: string | null;
  folderId?: string | null;
}

interface WorkflowStore {
  currentWorkflow: Workflow | null;
  nodes: Node[];
  edges: Edge[];
  selectedNode: Node | null;
  selectedEdge: Edge | null;
  highlightedNodeId: string | null;
  highlightedEdgeId: string | null;
  executionProgress: ExecutionProgress;
  currentWorkflowId: string | null;
  currentWorkflowMetadata: WorkflowMetadata | null;

  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (node: Node) => void;
  addEdge: (edge: Edge) => void;
  updateNode: (nodeId: string, data: Record<string, unknown>) => void;
  deleteNode: (nodeId: string) => void;
  deleteEdge: (edgeId: string) => void;
  removeEdge: (edgeId: string) => void; // Alias for deleteEdge
  setSelectedNode: (node: Node | null) => void;
  selectEdge: (edge: Edge | null) => void;
  setHighlightedNodeId: (nodeId: string | null) => void;
  setHighlightedEdgeId: (edgeId: string | null) => void;
  setCurrentWorkflowId: (workflowId: string | null) => void;
  setCurrentWorkflowMetadata: (metadata: WorkflowMetadata | null) => void;
  clearWorkflow: () => void;
  startExecution: (totalNodes: number) => void;
  updateExecutionProgress: (completedNodes: number) => void;
  completeExecution: () => void;
}

export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  currentWorkflow: null,
  nodes: [],
  edges: [],
  selectedNode: null,
  selectedEdge: null,
  highlightedNodeId: null,
  highlightedEdgeId: null,
  currentWorkflowId: null,
  currentWorkflowMetadata: null,
  executionProgress: {
    isExecuting: false,
    progress: 0,
    completedNodes: 0,
    totalNodes: 0,
  },

  setNodes: (nodes) => set({ nodes: typeof nodes === 'function' ? nodes(get().nodes) : nodes }),
  setEdges: (edges) => {
    const newEdges = typeof edges === 'function' ? edges(get().edges) : edges;
    const validatedEdges = validateEdges(newEdges, get().nodes);
    set({ edges: validatedEdges });
  },

  onNodesChange: (changes) => {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
    });
  },

  onEdgesChange: (changes) => {
    set({
      edges: applyEdgeChanges(changes, get().edges),
    });
  },

  onConnect: (connection) => {
    // Validate connection
    if (!connection.source || !connection.target) {
      return;
    }

    // Prevent duplicate connections
    const existingEdge = get().edges.find(
      edge => 
        edge.source === connection.source && 
        edge.target === connection.target &&
        edge.sourceHandle === connection.sourceHandle &&
        edge.targetHandle === connection.targetHandle
    );

    if (existingEdge) {
      return;
    }

    // Add the edge with proper type
    const connectionWithType = {
      ...connection,
      type: (connection as any).type || 'custom',
    };
    
    const newEdges = addEdge(connectionWithType, get().edges);
    
    set({
      edges: newEdges,
    });
    
  },

  addNode: (node) => {
    // Apply default property values if schema exists
    const nodeType = node.type || 'default';
    const propertyDefinitions = propertySchemaRegistry.getPropertyDefinitions(nodeType);

    if (propertyDefinitions.length > 0) {
      // For imported nodes, parameters may already have values - use those as base
      const currentParameters = (node.data as any)?.parameters || {};
      const currentProperties = (node.data as any)?.properties || {};
      // Merge: start with parameters, overlay with existing properties
      const baseProperties = { ...currentParameters, ...currentProperties };
      const propertiesWithDefaults = applyDefaultValues(baseProperties, propertyDefinitions);

      node = {
        ...node,
        data: {
          ...node.data,
          properties: propertiesWithDefaults,
        },
      };
    }

    // Also initialize default values from the new schema format into parameters
    // This ensures that default values like testInput are actually saved to the node
    const schemaResult = inferSchemaFromDefinition(nodeType);

    if (schemaResult.success && schemaResult.schema) {
      const currentParameters = (node.data as any)?.parameters || {};
      const parametersWithDefaults = { ...currentParameters };

      // Apply defaults from schema properties
      for (const property of schemaResult.schema.properties) {
        if (property.default !== undefined && parametersWithDefaults[property.key] === undefined) {
          parametersWithDefaults[property.key] = property.default;
        }
      }

      node = {
        ...node,
        data: {
          ...node.data,
          parameters: parametersWithDefaults,
        },
      };
    }

    set({
      nodes: [...get().nodes, node],
    });
  },

  addEdge: (edge) => {
    set({
      edges: [...get().edges, edge],
    });
  },

  updateNode: (nodeId, data) => {
    const updatedNodes = get().nodes.map((node) =>
      node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
    );

    const updatedSelectedNode = get().selectedNode?.id === nodeId
      ? updatedNodes.find((node) => node.id === nodeId) || null
      : get().selectedNode;

    set({
      nodes: updatedNodes,
      selectedNode: updatedSelectedNode,
    });
  },

  deleteNode: (nodeId) => {
    set({
      nodes: get().nodes.filter((node) => node.id !== nodeId),
      edges: get().edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
    });
  },

  deleteEdge: (edgeId) => {
    set({
      edges: get().edges.filter((edge) => edge.id !== edgeId),
    });
  },

  removeEdge: (edgeId) => {
    // Alias for deleteEdge
    set({
      edges: get().edges.filter((edge) => edge.id !== edgeId),
    });
  },

  setSelectedNode: (node) => set({ selectedNode: node }),

  selectEdge: (edge) => set({ selectedEdge: edge }),

  setHighlightedNodeId: (nodeId) => set({ highlightedNodeId: nodeId }),

  setHighlightedEdgeId: (edgeId) => set({ highlightedEdgeId: edgeId }),

  setCurrentWorkflowId: (workflowId) => set({ currentWorkflowId: workflowId }),

  setCurrentWorkflowMetadata: (metadata) => set({ currentWorkflowMetadata: metadata }),

  clearWorkflow: () => set({
    nodes: [],
    edges: [],
    selectedNode: null,
    selectedEdge: null,
    highlightedNodeId: null,
    highlightedEdgeId: null,
    currentWorkflowId: null,
    currentWorkflowMetadata: null,
    executionProgress: {
      isExecuting: false,
      progress: 0,
      completedNodes: 0,
      totalNodes: 0,
    },
  }),

  startExecution: (totalNodes) => {
    set({
      executionProgress: {
        isExecuting: true,
        progress: 0,
        completedNodes: 0,
        totalNodes,
        startTime: new Date(),
      },
    });
  },

  updateExecutionProgress: (completedNodes) => {
    const { totalNodes } = get().executionProgress;
    const progress = totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0;
    set({
      executionProgress: {
        ...get().executionProgress,
        completedNodes,
        progress,
      },
    });
  },

  completeExecution: () => {
    set({
      executionProgress: {
        isExecuting: false,
        progress: 100,
        completedNodes: get().executionProgress.totalNodes,
        totalNodes: get().executionProgress.totalNodes,
      },
    });
  },
}));
